import { normalizeWhitespace } from "../../../../helpers/stringHelpers.js";
import { trackEvent } from "../../trackEvent.js";

const getSlideData = (slide) => {
  if (!slide) return;

  const imgCarousel = slide.closest(".imageCarousel__container");
  const img = imgCarousel
    ? slide.querySelector(".image-container img")
    : slide.querySelector(".card__image img");
  const imagePath = img?.getAttribute("src") || "";

  // Derive imageName: prefer alt, then title, else filename from imagePath
  const imageTitle = img?.getAttribute("title")?.trim();
  const imageAlt = img?.getAttribute("alt")?.trim();
  const imageName =
    imageAlt ||
    imageTitle ||
    (() => {
      if (!imagePath) return "";
      const withoutQuery = imagePath.split("?")[0].split("#")[0];
      const segments = withoutQuery.split("/");
      try {
        return decodeURIComponent(segments[segments.length - 1] || "");
      } catch (e) {
        return segments[segments.length - 1] || "";
      }
    })();

  let slideTitle = slide.dataset.slideTitle || slide.getAttribute("slideTitle");
  if (!slideTitle || !slideTitle.trim()) {
    const heading = slide.querySelector("h1, h2, h3, h4, h5, h6");
    slideTitle = heading?.innerText?.trim() || "";
  }

  if (imgCarousel) {
    const productTitle = imgCarousel.dataset.itemName;
    if (productTitle) {
      slideTitle = productTitle;
    }
  }

  const link = slide.querySelector("a");
  const linkHref = link?.getAttribute("href") || "";
  const slideText = normalizeWhitespace(slide.innerText) || "";

  return { imagePath, imageName, slideTitle, linkHref, slideText };
};

const trackActiveSlide = (carousel, activeIndex) => {
  if (!carousel) return;

  const activeSlide =
    carousel.querySelectorAll(".carousel__slide")[activeIndex] ||
    carousel.querySelector(".carousel__slide.tns-slide-active");

  if (!activeSlide) return;

  const contentType = carousel.dataset.contentType;
  const isImageSlider = carousel.classList.contains('imageCarousel__container');

  if (contentType === "product" && isImageSlider) {
    const { imageName, slideTitle } = getSlideData(activeSlide);
    trackEvent({
      event: "click_item_carousel",
      event_type: "click",
      ...(imageName && { banner_name: imageName }),
      ...(slideTitle && { item_name: slideTitle }),
    });
  }
};

const trackSlide = (slide) => {
  if (!slide) return;

  const carousel = slide.closest(".carousel");
  const contentType = carousel?.dataset.contentType;
  const isImageSlider = carousel.classList.contains('imageCarousel__container');

  if (contentType === "product" && isImageSlider) {
    const { imageName, slideTitle } = getSlideData(slide);
    trackEvent({
      event: "click_item_carousel",
      event_type: "click",
      ...(imageName && { banner_name: imageName }),
      ...(slideTitle && { item_name: slideTitle }),
    });
  }
};

const trackCarouselSlide = (ctx) => {
  if (!ctx) {
    console.warn(
      "Context is not defined. Ensure you pass the Carousel context."
    );
    return;
  }

  const { container } = ctx.carousel?.getInfo()
  const carousel = container?.closest(`.carousel-${ctx.config.uuid}`)

  if (!carousel) {
    console.warn("Carousel element not found");
    return;
  }

  // Prevent duplicate event listeners by ensuring tracking is only initialized once per carousel instance.
  // This avoids multiple tracking events if trackCarouselSlide is called more than once.
  if (carousel.dataset.trackingInitialized) return;
  carousel.dataset.trackingInitialized = "true";

  // Attach click event listeners to each slide to track when a slide is clicked
  const slides = carousel.querySelectorAll(".carousel__slide");
  slides?.forEach((slide) => {
    slide.addEventListener("click", () => {
      trackSlide(slide);
    });
  });

  // Add click event listeners to prev/next arrow buttons in standard carousels to track the active slide after navigation
  const carouselControls = carousel.querySelector(".carousel__controls");

  if (carouselControls) {
    const prevButton = carouselControls.querySelector(
      "button.carousel__controls--prev"
    );
    const nextButton = carouselControls.querySelector(
      "button.carousel__controls--next"
    );

    [prevButton, nextButton].forEach((btn) => {
      btn?.addEventListener("click", (event) => {
        const activeIndex = ctx.carousel.getInfo().index;
        ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex, event));
      });
    });
  }

  // Add click event listeners to prev/next arrow buttons in image carousels to track the active slide after navigation
  const isImageCarousel = carousel.classList.contains(
    "imageCarousel__container"
  );

  if (isImageCarousel) {
    const imgNavButton = carousel.querySelectorAll(
      "button.imageCarousel__navButton"
    );

    imgNavButton?.forEach((btn) => {
      btn?.addEventListener("click", () => {
        const activeIndex = ctx.carousel.getInfo().index;
        ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex));
      });
    });
  }

  // Add click event listeners to navigation buttons (pagination dots) in Tiny Slider carousels to track the active slide
  const carouselNavs = carousel.querySelector(".tns-nav");

  if (carouselNavs) {
    const navButtons = carouselNavs.querySelectorAll("button");

    navButtons.forEach((navBtn) => {
      navBtn?.addEventListener("click", () => {
        const activeIndex = ctx.carousel.getInfo().index;
        ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex));
      });
    });
  }

  // Add click event listeners to navigation buttons in custom carousels to track the active slide
  const carouselNavs2 = carousel.querySelector(".carousel__nav");

  if (carouselNavs2) {
    const navButtons2 = carouselNavs2.querySelectorAll(
      "button.carousel__navButton"
    );

    navButtons2.forEach((navBtn) => {
      navBtn?.addEventListener("click", () => {
        const activeIndex = ctx.carousel.getInfo().index;
        ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex), 0);
      });
    });
  }

  // Add click event listeners to pagination buttons and use MutationObserver to handle dynamically rendered pagination
  const setupPaginationTracking = () => {
    const carouselPagination = carousel.querySelector(
      ".carousel__simplifiedPagination"
    );

    if (carouselPagination) {
      const paginationButtons = carouselPagination.querySelectorAll(
        "button:not([data-tracked])"
      );

      if (paginationButtons.length > 0) {
        paginationButtons.forEach((pagBtn) => {
          pagBtn.setAttribute("data-tracked", "true");
          pagBtn.addEventListener("click", () => {
            const activeIndex = ctx.carousel.getInfo().index;
            ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex));
          });
        });
      }
      return true;
    }
    return false;
  };

  // If pagination buttons are not found, observe the carousel for future changes and attach listeners when they appear
  if (!setupPaginationTracking()) {
    const observer = new MutationObserver(() => {
      if (setupPaginationTracking()) {
        observer.disconnect();
      }
    });
    observer.observe(carousel, {
      childList: true,
      subtree: true,
    });
  }

  // Add click event listeners to thumbnail buttons in image carousels to track the active slide when a thumbnail is clicked
  const imageCarouselThumbnail = carousel.querySelector(
    ".imageCarousel__thumbnail"
  );

  if (imageCarouselThumbnail) {
    const thumbnailButtons = imageCarouselThumbnail.querySelectorAll(
      "div[data-carousel-thumbnail]:not([data-tracked])"
    );

    if (thumbnailButtons.length > 0) {
      thumbnailButtons.forEach((thumbnailBtn) => {
        thumbnailBtn.setAttribute("data-tracked", "true");
        thumbnailBtn.addEventListener("click", () => {
          const activeIndex = ctx.carousel.getInfo().index;
          ctx.$nextTick(() => trackActiveSlide(carousel, activeIndex));
        });
      });
    }
  }
};

export { getSlideData, trackActiveSlide, trackSlide, trackCarouselSlide };
